import React, { useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { subscribeToLaunchNotify } from '../../util/api';
import {
  LAUNCH_RAILS,
  LAUNCH_RAIL_COMING_SOON,
  LAUNCH_RAIL_EDITORS_PICKS,
  LAUNCH_RAIL_LAUNCHING_TODAY,
  LAUNCH_RAIL_MOST_SAVED,
  LAUNCH_RAIL_NEW_THIS_WEEK,
  LAUNCH_RAIL_RECENTLY_ADDED,
  LAUNCH_REGION_OPTIONS,
  LAUNCH_TYPE_OPTIONS,
} from '../../config/configWhatsNew';

import { Page, LayoutSingleColumn } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import LaunchCard from '../../components/LaunchCard/LaunchCard';

import {
  computeLaunchRails,
  selectLaunchListings,
  selectLaunchSaveCounts,
} from './WhatsNewPage.duck';
import css from './WhatsNewPage.module.css';

// Rail key → heading. Kept as an explicit map rather than built from the key
// so a rail can be renamed in copy without touching the data vocabulary (the
// keys are part of the engagement metadata written to Postgres).
const RAIL_HEADINGS = {
  [LAUNCH_RAIL_LAUNCHING_TODAY]: 'WhatsNewPage.railLaunchingToday',
  [LAUNCH_RAIL_NEW_THIS_WEEK]: 'WhatsNewPage.railNewThisWeek',
  [LAUNCH_RAIL_COMING_SOON]: 'WhatsNewPage.railComingSoon',
  [LAUNCH_RAIL_MOST_SAVED]: 'WhatsNewPage.railMostSaved',
  [LAUNCH_RAIL_EDITORS_PICKS]: 'WhatsNewPage.railEditorsPicks',
  [LAUNCH_RAIL_RECENTLY_ADDED]: 'WhatsNewPage.railRecentlyAdded',
};

const asArray = v => (Array.isArray(v) ? v : v ? [v] : []);

// A newsletter signup has three honest outcomes and we say which happened:
// stored, already-on-the-list, or genuinely not stored (no warehouse
// configured, or the write failed). Nothing here ever claims a subscription
// that doesn't exist.
const NOTIFY_IDLE = 'idle';
const NOTIFY_SENDING = 'sending';
const NOTIFY_DONE = 'done';
const NOTIFY_DUPLICATE = 'duplicate';
const NOTIFY_FAILED = 'failed';

const NewsletterCta = () => {
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(NOTIFY_IDLE);

  const handleSubmit = e => {
    e.preventDefault();
    const address = email.trim();
    if (!address || status === NOTIFY_SENDING) return;
    setStatus(NOTIFY_SENDING);
    // launchId omitted ⇒ the general What's New newsletter (see launchNotify.js).
    subscribeToLaunchNotify({ email: address })
      .then(res => {
        if (!res || res.stored !== true) {
          setStatus(NOTIFY_FAILED);
          return;
        }
        setStatus(res.duplicate ? NOTIFY_DUPLICATE : NOTIFY_DONE);
      })
      .catch(() => setStatus(NOTIFY_FAILED));
  };

  const isSettled = status === NOTIFY_DONE || status === NOTIFY_DUPLICATE;

  return (
    <section className={css.newsletter}>
      <div className={css.newsletterInner}>
        <h2 className={css.newsletterTitle}>
          <FormattedMessage id="WhatsNewPage.newsletterTitle" />
        </h2>
        <p className={css.newsletterBody}>
          <FormattedMessage id="WhatsNewPage.newsletterBody" />
        </p>
        {isSettled ? (
          <p className={css.newsletterResult}>
            <FormattedMessage
              id={
                status === NOTIFY_DUPLICATE
                  ? 'WhatsNewPage.newsletterAlready'
                  : 'WhatsNewPage.newsletterThanks'
              }
            />
          </p>
        ) : (
          <form className={css.newsletterForm} onSubmit={handleSubmit}>
            <input
              className={css.newsletterInput}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={intl.formatMessage({ id: 'WhatsNewPage.newsletterPlaceholder' })}
              aria-label={intl.formatMessage({ id: 'WhatsNewPage.newsletterPlaceholder' })}
              required
            />
            <button
              className={css.newsletterButton}
              type="submit"
              disabled={status === NOTIFY_SENDING}
            >
              <FormattedMessage id="WhatsNewPage.newsletterSubmit" />
            </button>
          </form>
        )}
        {status === NOTIFY_FAILED ? (
          <p className={css.newsletterError}>
            <FormattedMessage id="WhatsNewPage.newsletterFailed" />
          </p>
        ) : null}
        <p className={css.newsletterFootnote}>
          <FormattedMessage id="WhatsNewPage.newsletterFootnote" />
        </p>
      </div>
    </section>
  );
};

// Placeholder cards while the query is in flight. Same box as a real card, so
// the page doesn't jump when the launches land.
const RailSkeleton = () => (
  <section className={css.rail}>
    <div className={css.railHead}>
      <span className={classNames(css.skeletonLine, css.skeletonHeading)} />
    </div>
    <div className={css.railTrack}>
      {[0, 1, 2, 3].map(i => (
        <div key={i} className={css.railItem}>
          <span className={css.skeletonImage} />
          <span className={classNames(css.skeletonLine, css.skeletonTitle)} />
          <span className={classNames(css.skeletonLine, css.skeletonMeta)} />
        </div>
      ))}
    </div>
  </section>
);

// Chip row for one client-side facet. Options are the values actually present
// in the fetched launches — offering a filter that can only ever return
// nothing is worse than not offering it.
const FilterChips = ({ labelId, options, current, onSelect }) =>
  options.length > 0 ? (
    <div className={css.chipGroup}>
      <span className={css.chipGroupLabel}>
        <FormattedMessage id={labelId} />
      </span>
      <div className={css.chipRow}>
        {options.map(([value, label]) => {
          const selected = current === value;
          return (
            <button
              key={value}
              type="button"
              className={classNames(css.chip, { [css.chipSelected]: selected })}
              aria-pressed={selected}
              onClick={() => onSelect(selected ? '' : value)}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

export const WhatsNewPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled, listings, saveCounts, fetchInProgress, fetchError } = props;

  const [typeFilter, setTypeFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');

  // Filters narrow the SET the rails are computed from, rather than filtering
  // each rail afterwards. Same rules, smaller input — so "Coming soon" under a
  // region filter still means "coming soon, soonest first", just for that
  // region, and a rail that empties out disappears exactly as it would
  // otherwise.
  const filtered = useMemo(
    () =>
      listings.filter(l => {
        const pd = l.attributes?.publicData || {};
        return (
          (!typeFilter || pd.launchType === typeFilter) &&
          (!regionFilter || asArray(pd.availabilityRegions).includes(regionFilter))
        );
      }),
    [listings, typeFilter, regionFilter]
  );

  const rails = useMemo(() => computeLaunchRails(filtered, saveCounts), [filtered, saveCounts]);

  // id → entity, so a rail's id list renders without a linear scan per card.
  const byId = useMemo(() => filtered.reduce((acc, l) => ({ ...acc, [l.id.uuid]: l }), {}), [
    filtered,
  ]);

  // Only offer facet values that occur in the fetched launches.
  const typeOptions = useMemo(
    () =>
      LAUNCH_TYPE_OPTIONS.filter(o =>
        listings.some(l => l.attributes?.publicData?.launchType === o.option)
      ).map(o => [o.option, o.label]),
    [listings]
  );
  const regionOptions = useMemo(
    () =>
      LAUNCH_REGION_OPTIONS.filter(o =>
        listings.some(l =>
          asArray(l.attributes?.publicData?.availabilityRegions).includes(o.option)
        )
      ).map(o => [o.option, o.label]),
    [listings]
  );

  const hasFilters = !!typeFilter || !!regionFilter;
  // LAUNCH_RAILS order IS page order (configWhatsNew). A rail with no members
  // is dropped here rather than rendered as an empty heading.
  const visibleRails = LAUNCH_RAILS.filter(key => (rails[key] || []).length > 0);
  const isEmpty = !fetchInProgress && !fetchError && visibleRails.length === 0;

  const title = intl.formatMessage({ id: 'WhatsNewPage.title' });
  const description = intl.formatMessage({ id: 'WhatsNewPage.description' });

  return (
    <Page
      title={title}
      description={description}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description,
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="WhatsNewPage" />}
        footer={<FooterContainer />}
      >
        <div className={css.pageRoot}>
          <header className={css.hero}>
            <span className={css.heroEyebrow}>
              <FormattedMessage id="WhatsNewPage.eyebrow" />
            </span>
            <h1 className={css.heroTitle}>
              <FormattedMessage id="WhatsNewPage.heading" />
            </h1>
            <p className={css.heroSub}>
              <FormattedMessage id="WhatsNewPage.subHeading" />
            </p>
          </header>

          {typeOptions.length > 0 || regionOptions.length > 0 ? (
            <div className={css.filters}>
              <FilterChips
                labelId="WhatsNewPage.filterType"
                options={typeOptions}
                current={typeFilter}
                onSelect={setTypeFilter}
              />
              <FilterChips
                labelId="WhatsNewPage.filterRegion"
                options={regionOptions}
                current={regionFilter}
                onSelect={setRegionFilter}
              />
              {hasFilters ? (
                <button
                  type="button"
                  className={css.clearFilters}
                  onClick={() => {
                    setTypeFilter('');
                    setRegionFilter('');
                  }}
                >
                  <FormattedMessage id="WhatsNewPage.clearFilters" />
                </button>
              ) : null}
            </div>
          ) : null}

          {fetchError ? (
            <p className={css.error}>
              <FormattedMessage id="WhatsNewPage.fetchFailed" />
            </p>
          ) : null}

          {fetchInProgress && listings.length === 0 ? (
            <>
              <RailSkeleton />
              <RailSkeleton />
            </>
          ) : null}

          {isEmpty ? (
            <div className={css.emptyState}>
              <p className={css.emptyTitle}>
                <FormattedMessage
                  id={hasFilters ? 'WhatsNewPage.emptyFilteredTitle' : 'WhatsNewPage.emptyTitle'}
                />
              </p>
              <p className={css.emptyBody}>
                <FormattedMessage
                  id={hasFilters ? 'WhatsNewPage.emptyFilteredBody' : 'WhatsNewPage.emptyBody'}
                />
              </p>
            </div>
          ) : null}

          {/* A rail with no members is not rendered — an empty "Launching
              today" heading would announce a promise the page can't keep. */}
          {visibleRails.map(railKey => (
            <section key={railKey} className={css.rail}>
              <div className={css.railHead}>
                <h2 className={css.railTitle}>
                  <FormattedMessage id={RAIL_HEADINGS[railKey]} />
                </h2>
              </div>
              {/* One markup, two behaviours: a snap-scrolling strip on mobile,
                  a grid on desktop (see the module CSS). */}
              <div className={css.railTrack}>
                {rails[railKey].map(id =>
                  byId[id] ? (
                    <LaunchCard
                      key={id}
                      className={css.railItem}
                      listing={byId[id]}
                      sourceSection={railKey}
                    />
                  ) : null
                )}
              </div>
            </section>
          ))}

          <NewsletterCta />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { fetchInProgress, fetchError } = state.WhatsNewPage;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    listings: selectLaunchListings(state),
    saveCounts: selectLaunchSaveCounts(state),
    fetchInProgress,
    fetchError,
  };
};

const WhatsNewPage = compose(connect(mapStateToProps))(WhatsNewPageComponent);

export default WhatsNewPage;
